/**
 * Hook to setup global error handlers
 *
 * Handles:
 * - Uncaught JavaScript errors (window.error)
 * - Unhandled promise rejections (unhandledrejection)
 * - Tracking errors to Application Insights
 *
 * Returns cleanup function
 */

import { useEffect } from 'react';
import { trackException } from '../services/AppInsightsPlugin';
import { Logger } from '../utils/Logger';

/**
 * Setup global error handlers for uncaught errors and unhandled rejections
 * All errors are tracked to Application Insights
 */
export function useGlobalErrorHandlers(): void {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      Logger.error('APP', 'Global error caught:', event.error);
      trackException(event.error || new Error(event.message), {
        type: 'uncaughtError',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      Logger.error('APP', 'Unhandled promise rejection:', event.reason);
      if (event.reason instanceof Error) {
        trackException(event.reason, {
          type: 'unhandledPromiseRejection'
        });
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
}
